import { forwardRef, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useMapbox } from "@/react/hooks/useMapbox";
import type { FormInputProps } from "@circle-react-uikit/Form/FormInput";
import { FormTextSelect } from "../FormTextSelect";

interface LocationInputProps extends FormInputProps {
  shouldSkipLocationDetailRetrieve?: boolean;
}

export const LocationInput = forwardRef<HTMLInputElement, LocationInputProps>(
  props => {
    const { name, shouldSkipLocationDetailRetrieve } = props;
    const { setValue, getValues } = useFormContext();
    const [searchValue, setSearchValue] = useState<string | null>(null);

    // We are using LocationInput for both as a Filter and as a CustomProfileField
    // When used in MapboxInputFilter, we need to get the value from the form context via getValues("value")?.name
    // When used in CustomField, we need to get the value from the form context via getValues(name) as a string
    const filterValue = getValues("value")?.name;
    const customFieldValue = getValues(name);

    const currentValue = useMemo(() => {
      if (filterValue) {
        return filterValue;
      }

      // We need this check because getValues(name) returns an object for Filters
      // and {} is not undefined
      if (typeof customFieldValue === "string") {
        return customFieldValue;
      }

      return "";
    }, [filterValue, customFieldValue]);

    const {
      locationOptions,
      handleSuggestionSelect,
      selectedMapboxId,
      debouncedSearch,
      value,
      displayValue,
      hasNoSuggestions,
    } = useMapbox({
      initialDisplayValue: currentValue,
      shouldSkipLocationDetailRetrieve,
    });

    useEffect(() => {
      if (value !== null) {
        setValue(name, value, { shouldValidate: true });
      }
    }, [value, name, setValue]);

    const handleSearchChange = (value: string) => {
      setSearchValue(value);
      void debouncedSearch(value);
    };

    return (
      // TODO: implement hiding caret for SearchableSelect
      <FormTextSelect
        name={name}
        hideLabel
        label=""
        placeholder={t("filters.location_filter.search_location")}
        emptyMessage={
          !searchValue
            ? t("filters.location_filter.search_helper")
            : hasNoSuggestions
              ? t("filters.location_filter.no_locations_found")
              : ""
        }
        options={locationOptions}
        onChange={handleSuggestionSelect}
        onSearchChange={handleSearchChange}
        value={selectedMapboxId}
        displayValue={displayValue}
      />
    );
  },
);

LocationInput.displayName = "LocationInput";
